/***
 * Created on 2025/4/29
 * @author: Adam Cheng <adamcheng@qnap.com>
 ***/
import React, {useMemo, useRef, useState} from 'react';
import CloudSpaceRegionComponent from "./CloudSpaceRegionComponent";
import {dataCellLabelToDataGridArea, normalizeSpaceSize} from "../../service/utils";
import CloudSpaceAssignUserStorageModal from "./CloudSpaceAssignUserStorageModal";
import {useNavigate as useReactRouterDomNavigator} from "react-router-dom";
import {useOrganization} from "../../context/OrganizationContext";
import {formatDateTime} from "../../service/timezone";

function CloudSpaceOrganizationUsageRowComponent({row_data, index, doRefresh, disPatcher, translate:t, isOwner, permissions, table_head_fields, state}) {
    const [showAssignStorage, setShowAssignStorage] = useState(false);
    const {getCurrentOrg} = useOrganization();
    const history = useReactRouterDomNavigator();
    const usageColRef = useRef(null);

    const storagePercentile = useMemo(() => {
        const total_used = row_data?.quota?.used || 0;
        const object = row_data?.quota?.used_by?.object || 0;
        const total = row_data?.quota?.total || 0;
        if (total <= 0) {
            return {
                total_used: 100,
                object: 0,
                storage: 100
            };
        }
        return {
            total_used: Math.ceil((total_used / total) * 100),
            object: Math.ceil((row_data?.quota?.used_by?.object || 0) / total * 100),
            storage: Math.ceil(((total_used-object) / total) * 100)
        };
    }, [row_data?.quota?.used, row_data?.quota?.total, row_data?.quota?.used_by?.object]);

    const goAccountList = (e) => {
        e.preventDefault();
        const org = getCurrentOrg();
        history(`/${org.organization_id}/account-list?filter=assigned`);
    }

    const moveOverPopover = (e) => {
        state.cancelHide();
        const percentile = storagePercentile.total_used;
        let popover_type = 'standard';
        if (percentile >= 0 && percentile < 90) {
            popover_type = 'standard';
            if (row_data?.quota?.used_by && row_data?.quota?.used_by?.object > 0) {
                popover_type = 'object_storage';
            }
        }
        else if (percentile >= 90 && percentile < 100) {
            popover_type = 'warning';
        }
        else if (percentile >= 100) {
            popover_type = 'error';
        }
        if (row_data?.quota?.total <= 0) {
            popover_type = 'no_access';
        }

        disPatcher({
            type: 'toggle_popover',
            payload: {
                show_popover: true,
                data: {
                    trigger_element: usageColRef.current,
                    popover_type,
                    row_data,
                }
            }
        });
    }

    const moveOutPopover = (e) => {
        const isInteractive = storagePercentile.total_used >= 90 || row_data?.quota?.total <= 0;
        state.scheduleHide(isInteractive ? 150 : 0);
    }

    return (
        <div className="flexbox-trow" key={index}>
            <div className={`tbody-cell ${table_head_fields[0].width_style}`} data-cell-label={t(table_head_fields[0].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[0].field_name)}>
                <CloudSpaceRegionComponent region_code={row_data.region} with_link={true}/>
            </div>
            <div className={`tbody-cell ${table_head_fields[1].width_style}`} data-cell-label={t(table_head_fields[1].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[1].field_name)}>
                <a href="#" onClick={goAccountList}>{row_data?.seats.used}</a>
            </div>
            <div className={`tbody-cell ${table_head_fields[2].width_style}`} data-cell-label={t(table_head_fields[2].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[2].field_name)}>
                {row_data?.seats?.total}
            </div>
            <div className={`tbody-cell ${table_head_fields[3].width_style}`} data-cell-label={t(table_head_fields[3].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[3].field_name)}>
                <div className="comp-storage-usage mt-4 mb-4 mr-16" ref={usageColRef} onMouseOver={moveOverPopover} onMouseOut={moveOutPopover}>
                    <div className="usage-value">
                        <div className={`usage-value__percentage ${storagePercentile.total_used >= 90 ? storagePercentile.total_used >= 100 ? 'color-red' : 'color-orange' : ''}`}>{storagePercentile.total_used}%</div>
                        <div className="usage-value__amount">
                            <span className="usage-value__amount__current">{normalizeSpaceSize(row_data?.quota?.used)}</span> / {normalizeSpaceSize(row_data?.quota?.total)}
                        </div>
                    </div>
                    <div className="usage-bar">
                        {storagePercentile?.object > 0 && <div className="usage-bar__obj" style={{width: storagePercentile?.object + "%"}}></div>}
                        <div className="usage-bar__storage" style={{width: storagePercentile.storage + '%'}}></div>
                    </div>
                    { storagePercentile?.object > 0 &&
                        <div className="usage-info">
                            <div className="usage-info__obj">{t('@Object@')}</div>
                            <div className="usage-info__storage">{t('@Storage@')}</div>
                        </div>
                    }
                </div>
            </div>
            <div className={`tbody-cell ${table_head_fields[4].width_style}`} data-cell-label={t(table_head_fields[4].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[4].field_name)}>
                {formatDateTime(row_data?.created_at)}
            </div>
            <div className={`tbody-cell ${table_head_fields[5].width_style}`} data-cell-label={t(table_head_fields[5].field_name)} data-grid-area={dataCellLabelToDataGridArea(table_head_fields[5].field_name)}>
                <div className="q-btn-group">
                    <button type="button" className="q-btn q-btn-primary q-btn-tiny q-btn-round-corner"
                            disabled={state?.isReadOnly}
                            onClick={e=>setShowAssignStorage(true)}>{t('@Assign Storage@')}</button>
                </div>
            </div>

            {
                showAssignStorage && <CloudSpaceAssignUserStorageModal openModal={showAssignStorage} closeModal={()=>setShowAssignStorage(false)} region_data={row_data}/>
            }
        </div>
    );
}

export default CloudSpaceOrganizationUsageRowComponent;